import { useEffect, useRef, useState } from 'react';
import {
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  TextField,
  InputAdornment,
  Avatar,
  Box,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import { useAppDispatch, useAppSelector } from '../../store';
import { findContactForm } from '../../store/contactForm/api';
import Loader from '../../components/Loader';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { getAssetURl } from '../../utils/url';
import {
  reset as resetContactForm,
  setStatus as setContactFormStatus,
} from '../../store/contactForm/contactFormSlice';
import {
  setLimit,
  setSkip,
  setCurrentPage,
} from '../../store/contactForm/contactFormSlice';
import CloseIcon from '@mui/icons-material/Close';
import { CONTACT_FORM_TYPE } from '../../constants';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.contactForm);
  const [search, setSearch] = useState<string>('');
  const [type, setType] = useState<string>('');
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findContactForm({
          ...(search && { search: search }),
          limit,
          skip,
          ...(type && { type: type }),
        })
      );
    }
  }, [dispatch, search, limit, skip, status, type]);

  useEffect(() => {
    dispatch(resetContactForm());
  }, [search]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setContactFormStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const boxRef = useRef<HTMLDivElement>(null);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [imageState, setImageState] = useState<string>();

  const handleOpenLightBox = (imageObject: string) => {
    setOpenLightBox(true);
    setImageState(imageObject);
  };

  const handleCloseLightBox = () => {
    setOpenLightBox(false);
    setImageState('');
  };

  return (
    <Page title={'Contact Form: List'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Contact Form'}
          links={[{ name: 'Dashboard', href: '/' }]}
        />

        <Card>
          <Grid
            container
            spacing={2}
            sx={{ py: 2.5, px: 3 }}
          >
            <Grid
              item
              xs={12}
              sm={6}
            >
              <FormControl fullWidth>
                <InputLabel>Select Type</InputLabel>
                <Select
                  id='type'
                  name='type'
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                  }}
                  label='Select Type'
                >
                  {CONTACT_FORM_TYPE.map((type) => {
                    return (
                      <MenuItem
                        key={type?.value}
                        value={type?.value}
                        sx={{
                          textTransform: 'capitalize',
                        }}
                      >
                        {type?.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                fullWidth
                onChange={(e) => setSearch(e.target.value)}
                placeholder={'Search...'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>{'Image'}</TableCell>
                    <TableCell align='center'>{'Name'}</TableCell>
                    <TableCell align='center'>{'Mobile No.'}</TableCell>
                    <TableCell align='center'>{'Type'}</TableCell>
                    <TableCell align='center'>{'Title'}</TableCell>
                    <TableCell align='center'>{'Description'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((contactForm) => (
                      <TableRow key={contactForm.id}>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {contactForm?.photoUrl && (
                            <Avatar
                              onClick={() =>
                                handleOpenLightBox(contactForm?.photoUrl)
                              }
                              alt={contactForm?.photoUrl}
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={getAssetURl(contactForm?.photoUrl, assetUrl)}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.createdByUserDetails?.firstName}{' '}
                          {contactForm?.createdByUserDetails?.lastName}
                        </TableCell>
                        <TableCell align='center'>
                          {
                            contactForm?.createdByUserDetails
                              ?.countryCallingCode
                          }
                          {'-'}
                          {contactForm?.createdByUserDetails?.mobileNumber}
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.type}
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.title}
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.description}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
      </Container>

      {openLightBox && (
        <Box
          sx={{ pt: 'env(safe-area-inset-top) !important' }}
          id='myModal'
          className='modal'
        >
          <span className='close cursor'>
            <CloseIcon
              fontSize='large'
              onClick={handleCloseLightBox}
              sx={{ mt: 'env(safe-area-inset-top) !important' }}
            />
          </span>
          <div
            className={`modal-content ${
              openLightBox ? 'display-block ' : 'display-none'
            }`}
          >
            {imageState && (
              <div className='vertical-center-div'>
                <div
                  ref={boxRef}
                  className={`mySlides ${
                    openLightBox ? 'display-block ' : 'display-none'
                  }`}
                >
                  <img
                    id={imageState}
                    src={getAssetURl(imageState, assetUrl)}
                    width='100%'
                    height='100%'
                    crossOrigin='anonymous'
                    alt={imageState}
                  />
                </div>
              </div>
            )}
          </div>
        </Box>
      )}
    </Page>
  );
};

export default Index;
